import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { TextTooltip } from '@ui/components/TextTooltip';
import { Interval } from '@ui/constants/poll';
import { useEntryPriceAfterSpread } from '@ui/features/Trade/hooks/useEntryPriceAfterSpread';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { PricesFactor } from '@ui/features/Trade/types';
import { useStateThrottle } from '@ui/hooks/useStateThrottle';
import { OpenPositionFee } from '@ui/utils/trade/getOpenPositionFee';
import BigNumber from 'bignumber.js';
import React, { useEffect, useMemo } from 'react';
import { css } from 'styled-system/css';
import { Box, Flex, HStack, VStack } from 'styled-system/jsx';

import { FeesBreakdown } from '../../FeeBreakdown';
import { useTotalOpenPositionFee } from '../hooks/useTotalOpenPositionFee';
import { useCollateralToken } from '../store/useCollateralToken';

const Fees = ({
  entryPrice,
  payTokenPrice,
  totalOpenPositionFee,
}: PricesFactor & {
  totalOpenPositionFee: OpenPositionFee | undefined;
}) => {
  return (
    <Flex justifyContent="flex-end">
      <TextTooltip
        content={
          <FeesBreakdown
            entryPrice={entryPrice}
            payTokenPrice={payTokenPrice}
          />
        }
        contentCss={css.raw({
          maxW: '300px',
        })}
      >
        <HStack>
          {
            !totalOpenPositionFee?.final.eq(totalOpenPositionFee?.base) && (
              <AmountUsd
                textAlign="end"
                textStyle="text.small"
                textDecoration="line-through"
                color="greyWhite.lightGrey1"
                value={totalOpenPositionFee?.base}
                data-cy="TotalFees"
                data-cy-value={totalOpenPositionFee?.base}
              />
            )
          }
          <AmountUsd
            textAlign="end"
            textStyle="text.small"
            color="greyWhite.lightGrey1"
            value={totalOpenPositionFee?.final}
            data-cy="TotalFees"
            data-cy-value={totalOpenPositionFee?.final}
          />
        </HStack>
      </TextTooltip>
    </Flex>
  );
};

const MemoFees = React.memo(Fees, (prevProps, nextProps) => {
  const areEqual =
    prevProps.entryPrice?.toString() == nextProps.entryPrice?.toString() &&
    prevProps.payTokenPrice?.toString() ==
    nextProps.payTokenPrice?.toString() &&
    prevProps.totalOpenPositionFee?.toString() ==
    nextProps.totalOpenPositionFee?.toString();

  return areEqual;
});

export const RealTimeFees = () => {
  const entryPrice = useEntryPriceAfterSpread();
  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const payTokenPrice = usePayTokenPriceLatest(payToken);
  const totalOpenPositionFee = useTotalOpenPositionFee({});

  return (
    <Fees
      totalOpenPositionFee={totalOpenPositionFee}
      entryPrice={entryPrice}
      payTokenPrice={payTokenPrice}
    />
  );
};
